import React, { useRef, useState } from 'react'; 
import { Plus, History } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { markAllNotificationsViewed } from '../../utils/api';
import { useChatPlatform } from '../context/ChatPlatformContext';
import { useSessions } from '../store/hooks';
import Tooltip from './Tooltip';

import { NebulaIcon, AnalysisIcon, PortfolioIcon } from './icons';


function HistoryPopover({ anchorRect, sessions, activeSessionId, onSelect, onClose }) {
    const maxHeight = Math.min(typeof window !== 'undefined' ? window.innerHeight * 0.7 : 500, 500);
    const top = anchorRect
        ? Math.max(8, Math.min(anchorRect.top, (typeof window !== 'undefined' ? window.innerHeight : 900) - maxHeight - 8))
        : 8;
    const left = anchorRect ? anchorRect.right + 8 : 60;

    return (
        <>
            <div className="fixed inset-0 z-[60]" onClick={onClose} />
            <div
                className="fixed w-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-[61] py-2"
                style={{ top, left, maxHeight }}
            >
                <div className="px-3 pb-1 text-[13px] font-semibold text-gray-500">History</div>
                {sessions.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400">Chưa có phiên chat</div>
                ) : (
                    sessions.map((s) => (
                        <button
                            key={s.session_id}
                            type="button"
                            onClick={() => { onSelect(s.session_id); onClose(); }}
                            className={`block w-full text-left px-3 py-1.5 text-[13px] truncate hover:bg-gray-50 transition-colors ${
                                s.session_id === activeSessionId
                                    ? 'text-gray-900 font-medium bg-gray-50'
                                    : 'text-gray-700'
                            }`}
                        >
                            {s.session_name || 'Untitled'}
                        </button>
                    ))
                )}
            </div>
        </>
    );
}

export default function SidebarMenuButtons({
    handleNewChat,
    isLoading,
    isInputDisabled,
    showNotificationPanel,
    setShowNotificationPanel,
    notificationCount,
    setNotificationCount,
    switchSession,
}) {
    const { isMobile, isSidebarOpen, setIsSidebarOpen, activeSessionId } = useChatPlatform();
    const sessions = useSessions();
    const [historyOpen, setHistoryOpen] = useState(false);
    const [anchorRect, setAnchorRect] = useState(null);
    const historyBtnRef = useRef(null);

    const toggleHistory = () => {
        if (historyOpen) {
            setHistoryOpen(false);
            return;
        }
        const rect = historyBtnRef.current?.getBoundingClientRect() || null;
        setAnchorRect(rect);
        setHistoryOpen(true);
    };

    const onNotificationClick = (closeSidebar) => {
        if (closeSidebar) setIsSidebarOpen(false);
        const opening = !showNotificationPanel;
        setShowNotificationPanel(opening);
        if (opening && notificationCount > 0) {
            markAllNotificationsViewed().catch(() => {});
            setNotificationCount(0);
        }
    };

    if (isMobile) {
        return (
            <div className="px-2 mt-2 space-y-1">
                <button
                    className="flex items-center gap-3 w-full px-2 py-2 rounded-md text-sm font-normal hover:bg-[#0000000c] transition-colors disabled:opacity-50 text-gray-800"
                    onClick={() => {
                        setIsSidebarOpen(false);
                        handleNewChat();
                    }}
                    disabled={isLoading || isInputDisabled}
                >
                    <div className="w-5 h-5 rounded-full bg-brand-blue-500 hover:bg-brand-blue-600 flex items-center justify-center flex-shrink-0 transition-colors">
                        <Plus className="w-3 h-3 text-white" />
                    </div>
                    <span className="whitespace-nowrap">Nebula ơi</span>
                </button>

                <div className="relative">
                    <button
                        className="flex items-center gap-3 w-full px-2 py-1.5 rounded-md text-sm font-normal hover:bg-[#0000000c] transition-colors text-gray-800"
                        onClick={() => onNotificationClick(true)}
                    >
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <NebulaIcon />
                        </div>
                        <span className="whitespace-nowrap">Hậu cung</span>
                        {notificationCount > 0 && (
                            <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">
                                {notificationCount > 10 ? '10+' : notificationCount}
                            </span>
                        )}
                    </button>
                </div>

                <div className="relative">
                    <NavLink
                        to="/portfolio-ai"
                        className={({ isActive }) =>
                            `flex items-center gap-3 w-full px-2 py-1.5 rounded-md text-sm font-normal transition-colors text-gray-800 ${isActive ? 'bg-app-hover' : 'hover:bg-[#0000000c]'}`
                        }
                        onClick={() => setIsSidebarOpen(false)}
                    >
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 relative">
                            <PortfolioIcon />
                        </div>
                        <span className="whitespace-nowrap">Tài sản</span>
                    </NavLink>
                </div>

                <div className="relative">
                    <NavLink
                        to="/insights"
                        className={({ isActive }) =>
                            `flex items-center gap-3 w-full px-2 py-1.5 rounded-md text-sm font-normal transition-colors text-gray-800 ${isActive ? 'bg-app-hover' : 'hover:bg-[#0000000c]'}`
                        }
                        onClick={() => setIsSidebarOpen(false)}
                    >
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 relative">
                            <AnalysisIcon />
                        </div>
                        <span className="whitespace-nowrap">Phân tích</span>
                    </NavLink>
                </div>
            </div>
        );
    }

    return (
        <div className="px-2 mt-2 space-y-1">
            <Tooltip content="Nebula ơi" disabled={isSidebarOpen}>
                <button
                    className={`flex items-center rounded-md text-sm font-normal transition-colors disabled:opacity-50 text-gray-800 ${isSidebarOpen ? 'gap-3 hover:bg-[#0000000c] w-full px-2 py-2' : 'justify-center hover:bg-app-hover w-9 h-9'}`}
                    onClick={handleNewChat}
                >
                    <div className="w-5 h-5 rounded-full bg-brand-blue-500 hover:bg-brand-blue-600 flex items-center justify-center flex-shrink-0 transition-colors">
                        <Plus className="w-3 h-3 text-white" />
                    </div>
                    {isSidebarOpen && <span className="whitespace-nowrap">Nebula ơi</span>}
                </button>
            </Tooltip>
            <div className="relative">
                <Tooltip content="Hậu cung" disabled={isSidebarOpen}>
                <button
                    className={`flex items-center rounded-md text-sm font-normal transition-colors text-gray-800 ${showNotificationPanel ? 'bg-app-hover' : ''} ${isSidebarOpen ? 'gap-3 hover:bg-[#0000000c] w-full px-2 py-1.5' : 'justify-center hover:bg-app-hover w-9 h-9'}`}
                    onClick={() => onNotificationClick(false)}
                >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 relative">
                        <NebulaIcon />
                        {notificationCount > 0 && !isSidebarOpen && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center">
                                {notificationCount > 10 ? '10+' : notificationCount}
                            </span>
                        )}
                    </div>
                    {isSidebarOpen && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="whitespace-nowrap">Hậu cung</span>
                            {notificationCount > 0 && (
                                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">
                                    {notificationCount > 10 ? '10+' : notificationCount}
                                </span>
                            )}
                        </div>
                    )}
                </button>
                </Tooltip>
            </div>
            <div className="relative">
                <Tooltip content="Tài sản" disabled={isSidebarOpen}>
                    <span className="block">
                        <NavLink
                            to="/portfolio-ai"
                            className={({ isActive }) =>
                                `flex items-center w-full px-2 py-1.5 rounded-md text-sm font-normal transition-colors text-gray-800 ${isActive ? 'bg-app-hover' : ''} ${isSidebarOpen ? 'gap-3 hover:bg-[#0000000c]' : 'justify-center hover:bg-app-hover'}`
                            }
                        >
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 relative">
                                <PortfolioIcon />
                            </div>
                            {isSidebarOpen && (
                                <div className="flex items-center justify-between flex-1">
                                    <span className="whitespace-nowrap">Tài sản</span>
                                </div>
                            )}
                        </NavLink>
                    </span>
                </Tooltip>
            </div>
            <div className="relative">
                <Tooltip content="Phân tích" disabled={isSidebarOpen}>
                    <span className="block">
                        <NavLink
                            to="/insights"
                            className={({ isActive }) =>
                                `flex items-center w-full px-2 py-1.5 rounded-md text-sm font-normal transition-colors text-gray-800 ${isActive ? 'bg-app-hover' : ''} ${isSidebarOpen ? 'gap-3 hover:bg-[#0000000c]' : 'justify-center hover:bg-app-hover'}`
                            }
                        >
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 relative">
                                <AnalysisIcon />
                            </div>
                            {isSidebarOpen && (
                                <div className="flex items-center justify-between flex-1">
                                    <span className="whitespace-nowrap">Phân tích</span>
                                </div>
                            )}
                        </NavLink>
                    </span>
                </Tooltip>
            </div>
            {!isSidebarOpen && (
                <div className="relative">
                    <Tooltip content="Lịch sử" disabled={historyOpen}>
                        <button
                            ref={historyBtnRef}
                            type="button"
                            className={`flex items-center w-full px-2 py-1.5 rounded-md text-sm font-normal transition-colors text-gray-800 justify-center hover:bg-app-hover ${historyOpen ? 'bg-app-hover' : ''}`}
                            onClick={toggleHistory}
                        >
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                <History size={20} strokeWidth={1.5} className="text-[#3C3C43]" />
                            </div>
                        </button>
                    </Tooltip>
                    {historyOpen && (
                        <HistoryPopover
                            anchorRect={anchorRect}
                            sessions={sessions}
                            activeSessionId={activeSessionId}
                            onSelect={(id) => switchSession?.(id)}
                            onClose={() => setHistoryOpen(false)}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
